import type { WidgetSize } from '@/components/Widget/Widget';

/** Broad asset category — drives the icon/label and whether volume bars make sense. */
export type AssetKind = 'etf' | 'equity' | 'commodity' | 'crypto' | 'ratio';

export interface KnownAsset {
  /** Stable key used in config + as the detail-modal selector id. */
  id: string;
  label: string;
  /** Yahoo Finance symbol. Empty for synthetic assets (ratios). */
  yahooSymbol: string;
  /** Display currency label. Empty = unitless (ratios). */
  currency: string;
  kind: AssetKind;
  hasVolume: boolean;
  /** For `kind: 'ratio'` — [numeratorSymbol, denominatorSymbol]. */
  ratioOf?: [string, string];
  nordnetUrl?: string;
}

export interface CustomAsset {
  symbol: string;
  label: string;
  currency?: string;
}

export interface WatchlistEntry {
  id: string;
  /** Present for user-added tickers not in {@link KNOWN_ASSETS}. */
  custom?: CustomAsset;
}

export type StocksLayout = 'single' | 'grid';

export interface StocksWidgetConfig {
  /** All configured assets (known-pool refs + custom). Order matters. */
  watchlist: WatchlistEntry[];
  /** Ids shown on the tile, in order. Sliced to the tile capacity at render. */
  visible: string[];
  tileSize: WidgetSize;
  layout: StocksLayout;
}

/** Yahoo symbols: letters, digits, dot, dash, equals, caret. */
const SYMBOL_RE = /^[A-Za-z0-9.=^-]{1,20}$/;

export const WIDGET_SIZES: WidgetSize[] = ['1x1', '2x1', '2x2', '3x1', '3x2'];

/** Selectable pool. Ratio assets reference two underlying Yahoo symbols. */
export const KNOWN_ASSETS: KnownAsset[] = [
  { id: 'sp500', label: 'S&P 500', yahooSymbol: 'SXR8.DE', currency: 'EUR', kind: 'etf', hasVolume: true },
  { id: 'gold', label: 'Gold', yahooSymbol: 'GC=F', currency: 'USD', kind: 'commodity', hasVolume: true },
  { id: 'silver', label: 'Silver', yahooSymbol: 'SI=F', currency: 'USD', kind: 'commodity', hasVolume: true },
  { id: 'rocketlab', label: 'Rocket Lab', yahooSymbol: 'RKLB', currency: 'USD', kind: 'equity', hasVolume: true },
  { id: 'bitcoin', label: 'Bitcoin', yahooSymbol: 'BTC-EUR', currency: 'EUR', kind: 'crypto', hasVolume: true },
  {
    id: 'gold-silver-ratio',
    label: 'Gold / Silver',
    yahooSymbol: '',
    currency: '',
    kind: 'ratio',
    hasVolume: false,
    ratioOf: ['GC=F', 'SI=F'],
  },
];

const KNOWN_BY_ID = new Map(KNOWN_ASSETS.map((asset) => [asset.id, asset]));

export const DEFAULT_STOCKS_CONFIG: StocksWidgetConfig = {
  watchlist: [{ id: 'sp500' }, { id: 'gold' }, { id: 'silver' }, { id: 'rocketlab' }, { id: 'bitcoin' }, { id: 'gold-silver-ratio' }],
  visible: ['sp500', 'gold', 'rocketlab', 'bitcoin'],
  tileSize: '2x2',
  layout: 'grid',
};

/** A watchlist entry resolved to a concrete, displayable asset. */
export interface ResolvedAsset {
  id: string;
  label: string;
  /** Yahoo symbol to fetch; '' for ratios. */
  yahooSymbol: string;
  currency: string;
  kind: AssetKind;
  hasVolume: boolean;
  ratioOf?: [string, string];
  nordnetUrl?: string;
}

/** Resolve a watchlist entry to a KnownAsset or a synthesized custom asset. */
export function resolveAsset(entry: WatchlistEntry): ResolvedAsset | null {
  const known = KNOWN_BY_ID.get(entry.id);
  if (known) return { ...known };
  if (entry.custom) {
    return {
      id: entry.id,
      label: entry.custom.label,
      yahooSymbol: entry.custom.symbol,
      currency: entry.custom.currency ?? '',
      kind: 'equity',
      hasVolume: true,
    };
  }
  return null;
}

/** Build the canonical config-stored id for a custom ticker. */
export function customAssetId(symbol: string): string {
  return `custom:${symbol.trim().toUpperCase()}`;
}

function sanitizeCustom(input: unknown): CustomAsset | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const candidate = input as Partial<CustomAsset>;
  const symbol = typeof candidate.symbol === 'string' ? candidate.symbol.trim() : '';
  const label = typeof candidate.label === 'string' ? candidate.label.trim() : '';
  if (!SYMBOL_RE.test(symbol) || !label) return undefined;
  const currency = typeof candidate.currency === 'string' && candidate.currency.trim() ? candidate.currency.trim().slice(0, 8) : undefined;
  const base: CustomAsset = { symbol, label: label.slice(0, 40) };
  return currency ? { ...base, currency } : base;
}

/** Defensive sanitizer so a malformed stored doc can never crash the widget. */
export function mergeStocksConfig(partial: Partial<StocksWidgetConfig> | null | undefined): StocksWidgetConfig {
  const p = partial ?? {};

  const rawWatchlist = Array.isArray(p.watchlist) ? p.watchlist : [];
  const watchlist: WatchlistEntry[] = [];
  const seen = new Set<string>();
  for (const raw of rawWatchlist) {
    if (!raw || typeof raw !== 'object') continue;
    const entry = raw as WatchlistEntry;
    const id = typeof entry.id === 'string' ? entry.id : '';
    if (!id || seen.has(id)) continue;
    const custom = sanitizeCustom(entry.custom);
    if (!KNOWN_BY_ID.has(id) && !custom) continue; // drop unknown non-custom ids
    seen.add(id);
    watchlist.push(custom ? { id, custom } : { id });
  }
  const effectiveWatchlist = watchlist.length > 0 ? watchlist : DEFAULT_STOCKS_CONFIG.watchlist;
  const watchlistIds = new Set(effectiveWatchlist.map((entry) => entry.id));

  let visible: string[];
  if (Array.isArray(p.visible)) {
    // Respect an explicit selection (even empty → empty state).
    const filtered = p.visible.filter((v): v is string => typeof v === 'string' && watchlistIds.has(v));
    visible = [...new Set(filtered)];
  } else {
    visible = effectiveWatchlist.map((entry) => entry.id);
  }

  const tileSize = WIDGET_SIZES.includes(p.tileSize as WidgetSize) ? (p.tileSize as WidgetSize) : DEFAULT_STOCKS_CONFIG.tileSize;
  const layout: StocksLayout = p.layout === 'single' || p.layout === 'grid' ? p.layout : DEFAULT_STOCKS_CONFIG.layout;

  return { watchlist: effectiveWatchlist, visible, tileSize, layout };
}

/** Number of asset cards a given tile size + layout can show. */
export function tileCapacity(size: WidgetSize, layout: StocksLayout): number {
  if (layout === 'single') return 1;
  const bySize: Record<WidgetSize, number> = { '1x1': 1, '2x1': 2, '2x2': 4, '3x1': 3, '3x2': 6 };
  return bySize[size];
}

/** Column count for the in-tile card grid, derived from the tile width. */
export function tileColumns(size: WidgetSize, layout: StocksLayout): number {
  if (layout === 'single') return 1;
  const bySize: Record<WidgetSize, number> = { '1x1': 1, '2x1': 2, '2x2': 2, '3x1': 3, '3x2': 3 };
  return bySize[size];
}
